import { inject } from '@angular/core';
import * as AlertActions from '@curbnturf/alert/src/lib/+state/alert.actions';
import { IReview } from '@curbnturf/entities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { ReviewService } from '../review.service';
import * as ReviewsActions from './reviews.actions';

export const createBoondockReview$ = createEffect(
  (actions$ = inject(Actions), reviewService = inject(ReviewService)) =>
    actions$.pipe(
      ofType(ReviewsActions.createBoondockReview),
      switchMap((action) =>
        reviewService.createBoondockReview(action.review, action.boondockSiteId).pipe(
          mergeMap((review: IReview) => [
            ReviewsActions.createBoondockReviewSuccess({ review }),
            AlertActions.displayAlert({
              title: 'Review Submitted',
              body: `Thank you for submitting your review. Your feedback helps others make informed decisions.`,
            }),
          ]),
          catchError((error) => of(ReviewsActions.createBoondockReviewFailed({ error }))),
        ),
      ),
      catchError((error) => of(ReviewsActions.createBoondockReviewFailed({ error }))),
    ),
  { functional: true },
);

export const createGuestReview$ = createEffect(
  (actions$ = inject(Actions), reviewService = inject(ReviewService)) =>
    actions$.pipe(
      ofType(ReviewsActions.createGuestReview),
      switchMap((action) =>
        reviewService.createGuestReview(action.review, action.guestId).pipe(
          mergeMap((review: IReview) => [
            ReviewsActions.createGuestReviewSuccess({ review }),
            AlertActions.displayAlert({
              title: 'Review Submitted',
              body: `Thank you for submitting your review. Your feedback helps others make informed decisions.`,
            }),
          ]),
          catchError((error) => of(ReviewsActions.createGuestReviewFailed({ error }))),
        ),
      ),
      catchError((error) => of(ReviewsActions.createGuestReviewFailed({ error }))),
    ),
  { functional: true },
);

export const createPoiReview$ = createEffect(
  (actions$ = inject(Actions), reviewService = inject(ReviewService)) =>
    actions$.pipe(
      ofType(ReviewsActions.createPoiReview),
      switchMap((action) =>
        reviewService.createPoiReview(action.review, action.poiId).pipe(
          mergeMap((review: IReview) => [
            ReviewsActions.createPoiReviewSuccess({ review }),
            AlertActions.displayAlert({
              title: 'Review Submitted',
              body: `Thank you for submitting your review. Your feedback helps others make informed decisions.`,
            }),
          ]),
          catchError((error) => of(ReviewsActions.createPoiReviewFailed({ error }))),
        ),
      ),
      catchError((error) => of(ReviewsActions.createPoiReviewFailed({ error }))),
    ),
  { functional: true },
);

export const createListingReview$ = createEffect(
  (actions$ = inject(Actions), reviewService = inject(ReviewService)) =>
    actions$.pipe(
      ofType(ReviewsActions.createListingReview),
      switchMap((action) =>
        reviewService.createListingReview(action.review, action.listingId).pipe(
          mergeMap((review: IReview) => [
            ReviewsActions.createListingReviewSuccess({ review }),
            AlertActions.displayAlert({
              title: 'Review Submitted',
              body: `Thank you for submitting your review. Your feedback helps others make informed decisions.`,
            }),
          ]),
          catchError((error) => of(ReviewsActions.createListingReviewFailed({ error }))),
        ),
      ),
      catchError((error) => of(ReviewsActions.createListingReviewFailed({ error }))),
    ),
  { functional: true },
);

export const loadListingReviews$ = createEffect(
  (actions$ = inject(Actions), reviewService = inject(ReviewService)) =>
    actions$.pipe(
      ofType(ReviewsActions.loadListingReviews),
      switchMap((action) =>
        reviewService.loadListingReviews(action.listingId).pipe(
          map((reviews: IReview[]) => ReviewsActions.loadListingReviewsSuccess({ reviews })),
          catchError((error) => of(ReviewsActions.loadListingReviewsFailure({ error }))),
        ),
      ),
      catchError((error) => of(ReviewsActions.loadListingReviewsFailure({ error }))),
    ),
  { functional: true },
);

export const loadGuestReviews$ = createEffect(
  (actions$ = inject(Actions), reviewService = inject(ReviewService)) =>
    actions$.pipe(
      ofType(ReviewsActions.loadGuestReviews),
      switchMap((action) =>
        reviewService.loadGuestReviews(action.guestId).pipe(
          map((reviews: IReview[]) => ReviewsActions.loadListingReviewsSuccess({ reviews })),
          catchError((error) => of(ReviewsActions.loadListingReviewsFailure({ error }))),
        ),
      ),
      catchError((error) => of(ReviewsActions.loadListingReviewsFailure({ error }))),
    ),
  { functional: true },
);

export const loadBoondockReviews$ = createEffect(
  (actions$ = inject(Actions), reviewService = inject(ReviewService)) =>
    actions$.pipe(
      ofType(ReviewsActions.loadBoondockReviews),
      switchMap((action) =>
        reviewService.loadBoondockReviews(action.boondockId).pipe(
          map((reviews: IReview[]) => ReviewsActions.loadListingReviewsSuccess({ reviews })),
          catchError((error) => of(ReviewsActions.loadListingReviewsFailure({ error }))),
        ),
      ),
      catchError((error) => of(ReviewsActions.loadListingReviewsFailure({ error }))),
    ),
  { functional: true },
);

export const loadPoiReviews = createEffect(
  (actions$ = inject(Actions), reviewService = inject(ReviewService)) =>
    actions$.pipe(
      ofType(ReviewsActions.loadPoiReviews),
      switchMap((action) =>
        reviewService.loadPoiReviews(action.activityId).pipe(
          map((reviews: IReview[]) => ReviewsActions.loadListingReviewsSuccess({ reviews })),
          catchError((error) => of(ReviewsActions.loadListingReviewsFailure({ error }))),
        ),
      ),
      catchError((error) => of(ReviewsActions.loadListingReviewsFailure({ error }))),
    ),
  { functional: true },
);
